<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
  selectedLabels: {
    type: Array,
    default: () => [],
  },
  // Status, ordenação e etiquetas só valem na listagem normal: com filtro
  // avançado ou pasta ativa quem manda é a query salva.
  showBasicFilters: {
    type: Boolean,
    default: true,
  },
  showLabelFilter: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'changeFilter',
  'update:selectedLabels',
  'openFilters',
  'toggleLayout',
]);

const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');
const labels = useMapGetter('labels/getLabels');

const [showMenu, toggleMenu] = useToggle();
const showLabelPicker = ref(false);
const labelSearch = ref('');

const currentStatusFilter = computed(
  () => chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN
);

const currentSortBy = computed(
  () => chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
);

const chatStatusOptions = computed(() =>
  ['open', 'resolved', 'pending', 'snoozed', 'all'].map(value => ({
    label: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`),
    value,
  }))
);

const chatSortOptions = computed(() =>
  [
    'last_activity_at_asc',
    'last_activity_at_desc',
    'created_at_desc',
    'created_at_asc',
    'priority_desc',
    'priority_asc',
    'priority_desc_created_at_asc',
    'waiting_since_asc',
    'waiting_since_desc',
  ].map(value => ({
    label: t(`CHAT_LIST.SORT_ORDER_ITEMS.${value}.TEXT`),
    value,
  }))
);

const activeChatStatusLabel = computed(
  () =>
    chatStatusOptions.value.find(m => m.value === chatStatusFilter.value)
      ?.label || ''
);

const activeChatSortLabel = computed(
  () =>
    chatSortOptions.value.find(m => m.value === chatSortFilter.value)?.label ||
    ''
);

const saveSelectedFilter = (type, value) => {
  updateUISettings({
    conversations_filter_by: {
      status: type === 'status' ? value : currentStatusFilter.value,
      order_by: type === 'sort' ? value : currentSortBy.value,
    },
  });
};

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};

const filteredLabels = computed(() => {
  const query = labelSearch.value.trim().toLowerCase();
  const available = query
    ? labels.value.filter(label => label.title.toLowerCase().includes(query))
    : labels.value;

  // Selecionadas no topo, para o filtro ativo ficar sempre visível.
  return [...available].sort((a, b) => {
    const aOn = props.selectedLabels.includes(a.title);
    const bOn = props.selectedLabels.includes(b.title);
    if (aOn !== bOn) return aOn ? -1 : 1;
    return a.title.localeCompare(b.title);
  });
});

const isLabelSelected = title => props.selectedLabels.includes(title);

const toggleLabel = title => {
  emit(
    'update:selectedLabels',
    isLabelSelected(title)
      ? props.selectedLabels.filter(label => label !== title)
      : [...props.selectedLabels, title]
  );
};

const clearLabels = () => emit('update:selectedLabels', []);

const closeMenu = () => {
  showMenu.value = false;
  showLabelPicker.value = false;
  labelSearch.value = '';
};

const onOpenFilters = () => {
  closeMenu();
  emit('openFilters');
};

const onToggleLayout = () => {
  closeMenu();
  emit('toggleLayout');
};
</script>

<template>
  <div class="relative flex">
    <NextButton
      id="toggleConversationFilterButton"
      v-tooltip.top-end="$t('CHAT_LIST.LIST_MENU.TOOLTIP_LABEL')"
      :aria-label="$t('CHAT_LIST.LIST_MENU.TOOLTIP_LABEL')"
      icon="i-lucide-ellipsis"
      slate
      faded
      xs
      @click="toggleMenu()"
    />
    <span
      v-if="selectedLabels.length"
      class="absolute -top-1 ltr:-right-1 rtl:-left-1 flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-n-brand text-white text-xxs pointer-events-none"
    >
      {{ selectedLabels.length }}
    </span>

    <div
      v-if="showMenu"
      v-on-click-outside="closeMenu"
      class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-2 absolute z-40 top-full ltr:right-0 rtl:left-0"
    >
      <template v-if="showBasicFilters">
        <div class="flex items-center justify-between gap-2 px-2 py-1.5">
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
          </span>
          <SelectMenu
            :model-value="chatStatusFilter"
            :options="chatStatusOptions"
            :label="activeChatStatusLabel"
            sub-menu-position="left"
            @update:model-value="handleStatusChange"
          />
        </div>
        <div class="flex items-center justify-between gap-2 px-2 py-1.5">
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
          </span>
          <SelectMenu
            :model-value="chatSortFilter"
            :options="chatSortOptions"
            :label="activeChatSortLabel"
            sub-menu-position="left"
            @update:model-value="handleSortChange"
          />
        </div>
      </template>

      <template v-if="showBasicFilters && showLabelFilter">
        <div class="my-1 border-t border-n-weak" />
        <button
          class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2"
          @click="showLabelPicker = !showLabelPicker"
        >
          <span class="i-lucide-tags size-4 text-n-slate-11 shrink-0" />
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.LABEL_FILTER.TITLE') }}
          </span>
          <span
            v-if="selectedLabels.length"
            class="flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-n-brand text-white text-xxs shrink-0"
          >
            {{ selectedLabels.length }}
          </span>
          <span
            class="size-4 shrink-0 ltr:ml-auto rtl:mr-auto text-n-slate-11"
            :class="
              showLabelPicker ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>

        <div v-if="showLabelPicker" class="px-2 pb-1">
          <div class="flex items-center gap-2 mb-2">
            <input
              v-model="labelSearch"
              type="text"
              :placeholder="$t('CHAT_LIST.LABEL_FILTER.SEARCH_PLACEHOLDER')"
              class="flex-1 min-w-0 h-7 rounded-md bg-n-slate-3 border border-transparent text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand px-2"
            />
            <button
              v-if="selectedLabels.length"
              class="text-xs text-n-brand hover:underline shrink-0"
              @click="clearLabels"
            >
              {{ $t('CHAT_LIST.LABEL_FILTER.CLEAR') }}
            </button>
          </div>
          <div class="overflow-y-auto max-h-56">
            <p
              v-if="!filteredLabels.length"
              class="py-3 text-sm text-center text-n-slate-11"
            >
              {{ $t('CHAT_LIST.LABEL_FILTER.NO_RESULTS') }}
            </p>
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2"
              @click="toggleLabel(label.title)"
            >
              <span
                class="flex items-center justify-center border rounded size-3.5 shrink-0"
                :class="
                  isLabelSelected(label.title)
                    ? 'bg-n-brand border-n-brand'
                    : 'border-n-strong'
                "
              >
                <span
                  v-if="isLabelSelected(label.title)"
                  class="text-white i-lucide-check size-3"
                />
              </span>
              <span
                class="rounded-full size-2 shrink-0"
                :style="{ backgroundColor: label.color }"
              />
              <span class="text-sm truncate text-n-slate-12">
                {{ label.title }}
              </span>
            </button>
          </div>
        </div>
      </template>

      <div class="my-1 border-t border-n-weak" />
      <button
        class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2"
        @click="onOpenFilters"
      >
        <span class="i-lucide-list-filter size-4 text-n-slate-11 shrink-0" />
        <span class="text-sm truncate text-n-slate-12">
          {{ $t('FILTER.TOOLTIP_LABEL') }}
        </span>
      </button>
      <button
        class="flex items-center w-full gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2 md:flex hidden"
        @click="onToggleLayout"
      >
        <span
          class="size-4 shrink-0 text-n-slate-11 rtl:rotate-180"
          :class="
            isOnExpandedLayout
              ? 'i-lucide-arrow-left-to-line'
              : 'i-lucide-arrow-right-to-line'
          "
        />
        <span class="text-sm truncate text-n-slate-12">
          {{ $t('CONVERSATION.SWITCH_VIEW_LAYOUT') }}
        </span>
      </button>
    </div>
  </div>
</template>
